import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Activity, ChevronDown, Search } from 'lucide-react'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Count,
  EmptyState,
  Page,
  PageHeader,
  Pager,
  StatCard,
  TableCard,
  Toolbar,
} from '@/components/shell'
import { api, type Paginated, type SystemLogRow, type SystemLogSummary } from '@/lib/api'
import { formatDateTime } from '@/lib/format'

export const Route = createFileRoute('/dashboard/system-logs')({
  validateSearch: (search: Record<string, unknown>): { search?: string } => ({
    search: typeof search.search === 'string' ? search.search.slice(0, 200) : undefined,
  }),
  component: SystemLogsPage,
})

const LEVEL_TONE: Record<string, string> = {
  error: 'bg-dn/15 text-dn hover:bg-dn/15',
  warn: 'bg-warn/15 text-warn hover:bg-warn/15',
  info: 'bg-edge/10 text-mut hover:bg-edge/10',
}

const LEVEL_LABEL: Record<string, string> = { error: '错误', warn: '警告', info: '信息' }

const CATEGORY_LABEL: Record<string, string> = {
  auth: '认证',
  http: '请求',
  ratelimit: '限流',
  system: '系统',
}

/**
 * 系统运行日志。
 *
 * 与审计日志的分工：那边记「谁对业务做了什么」（合规），这边记「系统自己发生了什么」
 * （运维）。分开是因为读者和保留策略都不同——审计要长期留存且不可篡改，
 * 系统日志是可轮转的运维数据。
 */
function SystemLogsPage() {
  const [page, setPage] = useState(1)
  const [level, setLevel] = useState('all')
  const [category, setCategory] = useState('all')
  const initialSearch = Route.useSearch().search
  const [search, setSearch] = useState(initialSearch ?? '')
  const [expanded, setExpanded] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['system-logs', { page, level, category, search }],
    queryFn: () =>
      api.get<Paginated<SystemLogRow>>('/system-logs', {
        page,
        perPage: 30,
        level: level === 'all' ? undefined : level,
        category: category === 'all' ? undefined : category,
        search: search || undefined,
      }),
    // 排障时希望看到新进来的记录
    refetchInterval: 30_000,
  })

  const { data: summary } = useQuery({
    queryKey: ['system-logs', 'summary'],
    queryFn: () => api.get<SystemLogSummary>('/system-logs/summary', { hours: 24 }),
    refetchInterval: 30_000,
  })

  return (
    <Page>
      <PageHeader
        title="系统日志"
        description="服务端运行记录：未处理异常、登录失败、限流触发。与审计日志分开——那边是合规留痕，这里是运维视角。"
      />

      <div className="grid gap-3.5 sm:grid-cols-3">
        <StatCard
          label="近 24 小时错误"
          value={summary?.error ?? '—'}
          tone={summary && summary.error > 0 ? 'rose' : 'default'}
          hint={summary && summary.error > 0 ? '需要关注' : '运行正常'}
        />
        <StatCard
          label="近 24 小时警告"
          value={summary?.warn ?? '—'}
          tone={summary && summary.warn > 0 ? 'warn' : 'default'}
          hint="登录失败与限流触发计入此项"
        />
        <StatCard label="近 24 小时信息" value={summary?.info ?? '—'} />
      </div>

      <Toolbar trailing={<Count value={data?.total} />}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-mut2" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            placeholder="搜索日志内容"
            className="h-9 w-60 rounded-apple pl-9 text-[13px]"
          />
        </div>

        <Select
          value={level}
          onValueChange={(v) => {
            setLevel(v)
            setPage(1)
          }}
        >
          <SelectTrigger className="h-9 w-28 rounded-apple text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-apple">
            <SelectItem value="all">全部级别</SelectItem>
            <SelectItem value="error">错误</SelectItem>
            <SelectItem value="warn">警告</SelectItem>
            <SelectItem value="info">信息</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={category}
          onValueChange={(v) => {
            setCategory(v)
            setPage(1)
          }}
        >
          <SelectTrigger className="h-9 w-28 rounded-apple text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-apple">
            <SelectItem value="all">全部分类</SelectItem>
            <SelectItem value="auth">认证</SelectItem>
            <SelectItem value="http">请求</SelectItem>
            <SelectItem value="ratelimit">限流</SelectItem>
            <SelectItem value="system">系统</SelectItem>
          </SelectContent>
        </Select>

        <span className="text-[12px] text-mut2">每 30 秒自动刷新</span>
      </Toolbar>

      <TableCard>
        {isLoading ? (
          <div className="space-y-2 p-6">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} className="h-9 w-full rounded-apple-sm" />
            ))}
          </div>
        ) : data?.data.length === 0 ? (
          <EmptyState
            icon={Activity}
            title="没有符合条件的日志"
            description="系统运行正常时这里会很安静——那是好事。"
          />
        ) : (
          <ul className="divide-y divide-line/40">
            {data?.data.map((row) => {
              const open = expanded === row.id
              const hasContext = row.context && Object.keys(row.context).length > 0

              return (
                <li key={row.id}>
                  <button
                    className="row-hover flex w-full items-start gap-3 px-6 py-3 text-left"
                    onClick={() => setExpanded(open ? null : row.id)}
                    disabled={!hasContext}
                  >
                    <Badge className={`mt-px h-5 shrink-0 text-[10.5px] ${LEVEL_TONE[row.level]}`}>
                      {LEVEL_LABEL[row.level] ?? row.level}
                    </Badge>

                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] leading-snug text-txt">{row.message}</div>
                      <div className="tnum mt-0.5 flex flex-wrap items-center gap-x-2.5 font-mono text-[10.5px] text-mut2">
                        <span>{CATEGORY_LABEL[row.category] ?? row.category}</span>
                        {row.actorUsername && <span>· {row.actorUsername}</span>}
                        {row.ipAddress && <span>· {row.ipAddress}</span>}
                      </div>
                    </div>

                    <span className="tnum shrink-0 font-mono text-[10.5px] text-dim">
                      {formatDateTime(row.createdAt)}
                    </span>

                    {hasContext && (
                      <ChevronDown
                        className={`mt-0.5 size-3.5 shrink-0 text-mut2 transition-transform ${
                          open ? 'rotate-180' : ''
                        }`}
                      />
                    )}
                  </button>

                  {open && hasContext && (
                    <pre className="mx-6 mb-3 max-h-64 overflow-auto rounded-apple-sm border border-line/70 bg-void p-3.5 font-mono text-[11px] leading-relaxed text-mut">
                      {JSON.stringify(row.context, null, 2)}
                    </pre>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </TableCard>

      <Pager page={page} pageCount={data?.pageCount ?? 1} onChange={setPage} />
    </Page>
  )
}
